import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import { selectIsCernerPatient } from '~/platform/user/cerner-dsot/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect } from 'react';

import { connectDrupalSourceOfTruthCerner } from '~/platform/utilities/cerner/dsot';

const useAcceleratedData = () => {
  const dispatch = useDispatch();

  const isAcceleratingCareNotesEnabled = useSelector(
    state =>
      state.featureToggles[
        FEATURE_FLAG_NAMES.mhvAcceleratedDeliveryCareNotesEnabled
      ],
  );

  const isVistaNotesFromV1Enabled = useSelector(
    state =>
      state.featureToggles[
        FEATURE_FLAG_NAMES.mhvMedicalRecordsGetAcceleratedVistaNotesFromV1
      ],
  );

  const isBlueButtonV2Enabled = useSelector(
    state => state.featureToggles[FEATURE_FLAG_NAMES.mhvBlueButtonUseV2],
  );

  const isBlueButtonV2ForNotesEnabled = useSelector(
    state =>
      state.featureToggles[FEATURE_FLAG_NAMES.mhvBlueButtonUseV2ForNotes],
  );

  useEffect(() => {
    // TECH DEBT: Do not trigger the connection when running unit tests because
    // the connection is not mocked and will cause the test to fail
    if (!window.Mocha) {
      // use Drupal based Cerner facility data
      connectDrupalSourceOfTruthCerner(dispatch);
    }
  }, [dispatch]);

  // This is based on the facilities list in the user's profile
  const isCerner = useSelector(selectIsCernerPatient);

  // This is based on the 200CRNR flag from MPI (set in vets-api)
  const isCernerUser = useSelector(
    state => state.user?.profile?.isCernerPatient,
  );

  const { featureToggles, drupalStaticData } = useSelector(state => state);
  const isLoading = useMemo(() => {
    // Treat uninitialized Drupal DSOT data as still loading.
    // Without this check, vamcEhrData being undefined causes isLoading
    // to return false prematurely, which allows isCerner to evaluate
    // as false before facility data arrives — hiding the
    // DateRangeSelector and falling back to the legacy data path.
    return (
      featureToggles.loading ||
      !drupalStaticData?.vamcEhrData ||
      drupalStaticData.vamcEhrData.loading
    );
  }, [drupalStaticData, featureToggles]);

  const hasEhrDataError = useMemo(() => {
    return !!drupalStaticData?.vamcEhrData?.error;
  }, [drupalStaticData]);

  // Users with the "VistA notes from v1" flag on keep today's behavior
  // (non-OH: v1 only; OH: hybrid). With it off, every user gets full v2.
  const isAcceleratingCareNotes = useMemo(() => {
    return (
      !!isAcceleratingCareNotesEnabled &&
      (!!isCernerUser || !isVistaNotesFromV1Enabled)
    );
  }, [isAcceleratingCareNotesEnabled, isCernerUser, isVistaNotesFromV1Enabled]);

  // Hybrid mode: OH notes from SCDF (v2), VistA notes from PHR (v1)
  const useV1ForVistaNotes = useMemo(() => {
    return !!isCernerUser && !!isVistaNotesFromV1Enabled;
  }, [isCernerUser, isVistaNotesFromV1Enabled]);

  const isAcceleratingBlueButton = useMemo(() => {
    return isBlueButtonV2Enabled;
  }, [isBlueButtonV2Enabled]);

  const isAcceleratingBlueButtonNotes = useMemo(() => {
    return isBlueButtonV2Enabled && isBlueButtonV2ForNotesEnabled;
  }, [isBlueButtonV2Enabled, isBlueButtonV2ForNotesEnabled]);

  // CCD V2 (Oracle Health) is enabled by the `200CRNR` flag from MPI (isCernerPatient)
  const isAcceleratingCCD = isCernerUser;

  return {
    isLoading,
    isCerner,
    hasEhrDataError,
    isAcceleratingCareNotes,
    useV1ForVistaNotes,
    isAcceleratingCCD,
    isAcceleratingBlueButton,
    isAcceleratingBlueButtonNotes,
  };
};

export default useAcceleratedData;
